import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { Box, Chip, CircularProgress, Tooltip } from "@mui/material";
import React from "react";

export type WordingScanChipState = "not_run" | "loading" | "done" | "error";

export type NeedsAttentionWordingChipProps = {
  scanState: WordingScanChipState;
  violationEntryCount: number;
  /** Busy/disabled appearance (matches Admin wording chip: loading or NA refresh wording). */
  busy: boolean;
  onClick?: (event: React.MouseEvent) => void;
  size?: "small" | "medium";
};

/**
 * Needs Attention header wording chip — reused in Wording guard → Wording violations accordion.
 */
export default function NeedsAttentionWordingChip({
  scanState,
  violationEntryCount,
  busy,
  onClick,
  size = "small",
}: NeedsAttentionWordingChipProps) {
  const wordingChipBusy = busy;

  const tooltipTitle =
    wordingScanStateTitle(scanState, violationEntryCount, wordingChipBusy);

  return (
    <Tooltip title={tooltipTitle}>
      <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
        <Chip
          size={size}
          color={
            wordingChipBusy
              ? "default"
              : scanState === "error"
                ? "warning"
                : scanState === "not_run"
                  ? "default"
                  : violationEntryCount > 0
                    ? "warning"
                    : "success"
          }
          icon={
            wordingChipBusy ? (
              <CircularProgress size={12} thickness={5} sx={{ color: "inherit", ml: 0.25 }} />
            ) : scanState === "not_run" ? (
              <PlayCircleOutlineIcon sx={{ fontSize: "1rem !important", ml: 0.25 }} />
            ) : scanState === "error" ? (
              <ErrorOutlineIcon sx={{ fontSize: "1rem !important", ml: 0.25 }} />
            ) : undefined
          }
          label={
            wordingChipBusy
              ? "Scanning wording…"
              : scanState === "error"
                ? "Wording scan failed — tap to retry"
                : scanState === "not_run"
                  ? "Wording violations — tap to run scan"
                  : `Wording violations: ${violationEntryCount}`
          }
          onClick={onClick}
          clickable={Boolean(onClick)}
        />
      </Box>
    </Tooltip>
  );
}

function wordingScanStateTitle(
  wordingScanState: WordingScanChipState,
  wordingViolationEntryCount: number,
  wordingChipBusy: boolean
): string {
  if (wordingChipBusy) {
    return "Wording scan in progress…";
  }
  if (wordingScanState === "not_run") {
    return "Wording analysis does not run on page load. Click to open wording guard and start a scan.";
  }
  if (wordingScanState === "error") {
    return "Last wording scan failed. Click to retry.";
  }
  if (wordingViolationEntryCount > 0) {
    return `${wordingViolationEntryCount} entries with violations. Click to open wording guard.`;
  }
  return "No wording violations in the latest scan. Click to open wording guard.";
}
